import { ReactElement, useState } from 'react';
import { useSearchParams } from 'react-router';
import {
    UIDrawer,
    UIDrawerContent,
    UIDrawerHeader,
    UIEmptyState,
    UIIcon,
    UISection,
    UISectionContent,
    UISectionHeader,
    UITableHeaderCell,
    UIText
} from '@/ui';
import {
    charcoal,
    charcoalLight,
    concrete,
    mint,
    opacity,
    red,
    yellow
} from '@/ui/styles';
import { useCompaniesTranslation } from '../hooks';
import { useFormatDate } from '@/common/hooks';
import { AppDevice } from '../queries';
import {
    ANDROID_GROUPS,
    AppDeviceGroupDescriptor,
    AppDeviceSettingHealth,
    AppDeviceSettingsValues,
    evaluateField,
    getWorstHealth,
    resolveFieldValue,
    IOS_GROUPS
} from '../util/app-device-settings';
import styles from './member-app-devices-section.module.scss';

type AppDevicesSectionProps = {
    appDevices: AppDevice[];
};

/** Presentation for each classification — the classifying itself is in util. */
const HEALTH_COLORS: Record<AppDeviceSettingHealth, string> = {
    good: mint,
    warning: yellow,
    bad: red,
    neutral: charcoalLight,
    notApplicable: concrete,
    unknown: concrete
};

/**
 * The pill behind each status: the same hue as its dot, dropped to a tint so
 * the dot still reads as the strongest mark. Text stays charcoal — mint and
 * yellow don't have the contrast to be legible as text at this size.
 */
const HEALTH_BACKGROUND_COLORS: Record<AppDeviceSettingHealth, string> = {
    good: mint + opacity(0.14),
    warning: yellow + opacity(0.18),
    bad: red + opacity(0.12),
    neutral: charcoal + opacity(0.06),
    notApplicable: charcoal + opacity(0.06),
    unknown: charcoal + opacity(0.06)
};

/** The settings tree, read loosely — see the note on `settings` below. */
function getGroups(appDevice: AppDevice): AppDeviceGroupDescriptor[] {
    return appDevice.platform === 'ios' ? IOS_GROUPS : ANDROID_GROUPS;
}

/**
 * The settings object is a loose bag of optional string leaves, so it is read
 * through an index signature rather than the per-platform types — that is what
 * lets one descriptor table drive both platforms.
 */
function getSettings(appDevice: AppDevice): AppDeviceSettingsValues {
    return appDevice.settings as AppDeviceSettingsValues;
}

/**
 * The handsets a user has signed in to the mobile app on. Read-only — the app
 * is the only thing that writes these, via PUT /v3/me/app-devices.
 *
 * Selecting a row opens a drawer with the permissions and power settings that
 * handset last reported, which is usually where the answer lives when a user
 * says they never got an alert.
 */
export function MemberAppDevicesSection(
    props: AppDevicesSectionProps
): ReactElement {
    const { appDevices } = props;

    const { t } = useCompaniesTranslation();

    // The selection lives in the URL (?device=<id>) so an open drawer survives
    // refresh and can be shared; held by id rather than by object so the drawer
    // keeps showing fresh data if the query refetches underneath it. An id that
    // doesn't match any device — a stale link, or the list still loading — just
    // means no drawer.
    const [searchParams, setSearchParams] = useSearchParams();
    const selectedId = searchParams.get('device');
    const selected =
        appDevices.find((appDevice) => appDevice.id === selectedId) ?? null;

    function setSelectedId(id: string | null) {
        setSearchParams((prev) => {
            if (id === null) {
                prev.delete('device');
            } else {
                prev.set('device', id);
            }
            return prev;
        });
    }

    return (
        <>
            <UISection>
                <UISectionHeader title={t('shared.labels.app_devices')} />
                {appDevices.length === 0 ? (
                    <UISectionContent>
                        <UIEmptyState
                            description={t(
                                'components.member_app_devices_section.no_devices'
                            )}
                            emoji="📱"
                        />
                    </UISectionContent>
                ) : (
                    <table className={styles.deviceTable}>
                        <thead>
                            <tr>
                                <UITableHeaderCell
                                    text={t(
                                        'components.member_app_devices_section.device_column'
                                    )}
                                />
                                <UITableHeaderCell
                                    text={t(
                                        'components.member_app_devices_section.os_column'
                                    )}
                                    tooltip={t(
                                        'components.member_app_devices_section.os_column_tooltip'
                                    )}
                                />
                                <UITableHeaderCell
                                    text={t(
                                        'components.member_app_devices_section.app_column'
                                    )}
                                    tooltip={t(
                                        'components.member_app_devices_section.app_column_tooltip'
                                    )}
                                />
                                <UITableHeaderCell
                                    text={t(
                                        'components.member_app_devices_section.last_seen_column'
                                    )}
                                />
                                <UITableHeaderCell
                                    text={t(
                                        'components.member_app_devices_section.settings_column'
                                    )}
                                    tooltip={t(
                                        'components.member_app_devices_section.settings_column_tooltip'
                                    )}
                                />
                                <th />
                            </tr>
                        </thead>
                        <tbody>
                            {appDevices.map((appDevice) => (
                                <AppDeviceTableRow
                                    key={appDevice.id}
                                    appDevice={appDevice}
                                    onSelect={() => setSelectedId(appDevice.id)}
                                />
                            ))}
                        </tbody>
                    </table>
                )}
            </UISection>

            <AppDeviceDrawer
                appDevice={selected}
                onClose={() => setSelectedId(null)}
            />
        </>
    );
}

function AppDeviceTableRow(props: {
    appDevice: AppDevice;
    onSelect: () => void;
}): ReactElement {
    const { appDevice, onSelect } = props;

    const { t } = useCompaniesTranslation();
    const { formatSmartShortTimeAndDate } = useFormatDate();

    const worstHealth = getWorstHealth(
        getGroups(appDevice),
        getSettings(appDevice),
        appDevice.osVersion
    );

    return (
        <tr className={styles.deviceRow} onClick={onSelect}>
            <td>
                <div className={styles.deviceCell}>
                    <UIIcon name="smartphone" size={18} color={charcoal} />
                    <div className={styles.deviceNames}>
                        <UIText medium>{appDevice.deviceName}</UIText>
                        <UIText type="default2" variant="small">
                            {`${appDevice.manufacturer} ${appDevice.model}`}
                        </UIText>
                    </div>
                </div>
            </td>
            <td>
                <UIText type="default2">
                    {`${t(`components.member_app_devices_section.platform_${appDevice.platform}`)} ${appDevice.osVersion}`}
                </UIText>
            </td>
            <td>
                <UIText type="default2">
                    {t('components.member_app_devices_section.app_version', {
                        version: appDevice.appVersion,
                        build: appDevice.appBuild
                    })}
                </UIText>
            </td>
            <td>
                <UIText type="default2">
                    {formatSmartShortTimeAndDate(new Date(appDevice.lastSeen))}
                </UIText>
            </td>
            <td>
                <HealthPill
                    health={worstHealth}
                    text={t(
                        `components.member_app_devices_section.health_${worstHealth}`
                    )}
                />
            </td>
            <td>
                <UIIcon name="chevron-right" size={18} color={charcoal} />
            </td>
        </tr>
    );
}

/**
 * Openness is derived from a non-null device, matching how the other modals in
 * this directory work. The device is kept rendered while closing so the exit
 * animation doesn't play against an empty panel.
 */
function AppDeviceDrawer(props: {
    appDevice: AppDevice | null;
    onClose: () => void;
}): ReactElement {
    const { appDevice, onClose } = props;

    const { t } = useCompaniesTranslation();
    const { formatSmartShortTimeAndDate } = useFormatDate();

    const [lastDevice, setLastDevice] = useState<AppDevice | null>(null);
    if (appDevice !== null && appDevice !== lastDevice) {
        setLastDevice(appDevice);
    }

    const device = appDevice ?? lastDevice;

    return (
        <UIDrawer isOpen={appDevice !== null} onClose={onClose}>
            {device && (
                <>
                    <UIDrawerHeader
                        title={device.deviceName}
                        subtitle={`${device.manufacturer} ${device.model} · ${t(
                            `components.member_app_devices_section.platform_${device.platform}`
                        )} ${device.osVersion}`}
                        onClose={onClose}
                    />
                    <UIDrawerContent>
                        <div className={styles.drawerMeta}>
                            <SettingRow
                                label={t(
                                    'components.member_app_devices_section.app_column'
                                )}
                                value={t(
                                    'components.member_app_devices_section.app_version',
                                    {
                                        version: device.appVersion,
                                        build: device.appBuild
                                    }
                                )}
                            />
                            <SettingRow
                                label={t(
                                    'components.member_app_devices_section.last_seen_column'
                                )}
                                value={formatSmartShortTimeAndDate(
                                    new Date(device.lastSeen)
                                )}
                            />
                        </div>

                        {getSettings(device) === undefined ? (
                            <UIText type="default2">
                                {t(
                                    'components.member_app_devices_section.no_settings'
                                )}
                            </UIText>
                        ) : (
                            getGroups(device).map((group) => (
                                <SettingsGroup
                                    key={group.key}
                                    group={group}
                                    values={getSettings(device)?.[group.key]}
                                    osVersion={device.osVersion}
                                />
                            ))
                        )}
                    </UIDrawerContent>
                </>
            )}
        </UIDrawer>
    );
}

/**
 * A group the app didn't report at all is dropped, and so is any field the OS
 * is too old to have — the drawer header states the OS version, so listing
 * permissions that cannot exist on it is just noise.
 *
 * A field the OS *does* have but the app didn't report is still shown, as
 * unknown: "this build never told us" is worth seeing when debugging a missed
 * alert.
 */
function SettingsGroup(props: {
    group: AppDeviceGroupDescriptor;
    values: Record<string, string | undefined> | undefined;
    osVersion: string;
}): ReactElement | null {
    const { group, values, osVersion } = props;

    const { t } = useCompaniesTranslation();

    if (values === undefined) {
        return null;
    }

    const visibleFields = group.fields
        .map((field) => ({
            field,
            value: resolveFieldValue(field, values),
            health: evaluateField(field, values, osVersion)
        }))
        .filter(({ health }) => health !== 'notApplicable');

    // Every field in this group predates the device's OS version, so there is
    // nothing left to show.
    if (visibleFields.length === 0) {
        return null;
    }

    return (
        <div className={styles.group}>
            <UIText medium>
                {t(`components.member_app_devices_section.group_${group.key}`)}
            </UIText>
            {visibleFields.map(({ field, value, health }) => {
                // Unmapped values fall back to the raw string, so a value the
                // server starts sending before we add copy for it still reads
                // as something.
                const text =
                    value === undefined
                        ? '—'
                        : t(
                              `components.member_app_devices_section.value_${value}`,
                              {
                                  defaultValue: value
                              }
                          );

                return (
                    <SettingRow
                        key={field.key}
                        label={t(
                            `components.member_app_devices_section.${group.key}_${field.key}`
                        )}
                        value={text}
                        health={health}
                    />
                );
            })}
        </div>
    );
}

function SettingRow(props: {
    label: string;
    value: string;
    /** Omitted for rows that aren't a setting, which then get no dot column. */
    health?: AppDeviceSettingHealth;
}): ReactElement {
    const { label, value, health } = props;

    return (
        <div className={styles.settingRow}>
            <div className={styles.settingLabel}>
                <UIText type="default2" variant="small">
                    {label}
                </UIText>
            </div>
            <div className={styles.settingValue}>
                {health ? (
                    <HealthPill health={health} text={value} />
                ) : (
                    <UIText medium variant="small">
                        {value}
                    </UIText>
                )}
            </div>
        </div>
    );
}

/** The status text and its dot, on a tint of the dot's own colour. */
function HealthPill(props: {
    health: AppDeviceSettingHealth;
    text: string;
}): ReactElement {
    const { health, text } = props;

    return (
        <span
            className={styles.pill}
            style={{ backgroundColor: HEALTH_BACKGROUND_COLORS[health] }}
        >
            <UIText medium variant="small">
                {text}
            </UIText>
            <StatusDot health={health} />
        </span>
    );
}

function StatusDot(props: { health: AppDeviceSettingHealth }): ReactElement {
    return (
        <span
            className={styles.dot}
            style={{
                backgroundColor: HEALTH_COLORS[props.health]
            }}
        />
    );
}
